import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Sparkles, Loader2, Eye, FileDown, Globe, Lock, Link2,
  ChevronUp, ChevronDown, Trash2, Plus, ImagePlus, Video, X, Check, Archive, BarChart3, MessageSquare,
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import { StoryRender, ShareButtons, dateDisplay, mediaUrl } from "../components/story/StoryShared";

const inp = "border border-zinc-300 px-2.5 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary bg-white";
const AI_ACTIONS = [["rewrite", "Rewrite"], ["shorten", "Shorten"], ["expand", "Expand"], ["personal", "More Personal"], ["informative", "More Informative"], ["grammar", "Fix Grammar"]];
const PDF_THEMES = ["classic", "modern", "minimal", "photobook"];

/* Story Studio — edit, preview, publish, share & export. Draft stays private until "Publish Story". */
export default function StoryStudio() {
  const { sid } = useParams();
  const [story, setStory] = useState(null);
  const [saveState, setSaveState] = useState("saved");
  const [preview, setPreview] = useState(false);
  const [publishBox, setPublishBox] = useState(null); // {visibility, review?, done?}
  const [pdfTheme, setPdfTheme] = useState("classic");
  const [translating, setTranslating] = useState(false);
  const timer = useRef(null);

  useEffect(() => { api.get(`/stories/${sid}`).then((r) => setStory(r.data)).catch(() => toast.error("Story not found")); }, [sid]);

  const persist = useCallback(async (s) => {
    setSaveState("saving");
    try {
      await api.put(`/stories/${sid}`, {
        title: s.title, subtitle: s.subtitle, sections: s.sections, media: s.media,
        cover_media_id: s.cover_media_id, author_display: s.author_display, date_display_mode: s.date_display_mode,
      });
      setSaveState("saved");
    } catch { setSaveState("dirty"); toast.error("Autosave failed — retrying on next change"); }
  }, [sid]);

  const patch = (fields) => {
    setStory((s) => {
      const next = { ...s, ...fields };
      setSaveState("dirty");
      clearTimeout(timer.current);
      timer.current = setTimeout(() => persist(next), 1200);
      return next;
    });
  };
  const patchSection = (id, f) => patch({ sections: story.sections.map((x) => (x.id === id ? { ...x, ...f } : x)) });
  const moveSection = (i, dir) => {
    const arr = [...story.sections];
    const j = i + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[i], arr[j]] = [arr[j], arr[i]];
    patch({ sections: arr });
  };

  if (!story) return <div className="max-w-4xl mx-auto px-5 py-16 text-sm text-muted-foreground">Loading story…</div>;

  const published = story.status === "published";
  const publicUrl = story.public_id ? `${window.location.origin}/story/${story.public_id}` : null;
  const cardUrl = story.public_id ? `${API}/stories/share/${story.public_id}` : null;
  const findings = story.privacy_scan?.findings || [];

  const removeMedia = (mid) => patch({
    media: story.media.filter((x) => x.id !== mid),
    cover_media_id: story.cover_media_id === mid ? null : story.cover_media_id,
    sections: story.sections.map((x) => ({ ...x, media_ids: (x.media_ids || []).filter((id) => id !== mid) })),
  });

  const translate = async (language) => {
    if (translating) return;
    setTranslating(true);
    try {
      toast.info(`Translating to ${language}… this takes up to a minute`);
      const r = await api.post(`/stories/${sid}/translate`, { language });
      toast.success(`${language} copy created — opening it now`);
      window.location.href = `/stories/${r.data.id}/edit`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
      setTranslating(false);
    }
  };

  const exportPdf = async () => {
    const token = localStorage.getItem("token") || "";
    window.open(`${API}/stories/${sid}/pdf?auth=${token}&theme=${pdfTheme}`, "_blank");
  };

  const doPublish = async (visibility, acknowledged = false) => {
    try {
      const r = await api.post(`/stories/${sid}/publish`, { visibility, acknowledged });
      if (r.data.requires_review) { setPublishBox({ visibility, review: r.data }); return; }
      setStory((s) => ({ ...s, status: "published", visibility, public_id: r.data.public_id }));
      setPublishBox({ visibility, done: r.data });
      toast.success("Story published");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); }
  };
  const unpublish = async () => {
    await api.post(`/stories/${sid}/unpublish`);
    setStory((s) => ({ ...s, status: "draft", visibility: "private" }));
    setPublishBox(null);
    toast.success("Story is private again — public access stopped");
  };

  const view = {
    story_id: story.id, title: story.title, subtitle: story.subtitle,
    author_display: story.author_display || "Anonymous Traveler",
    date_display: dateDisplay(story.trip_start, story.trip_end, story.date_display_mode),
    cover_media_id: story.cover_media_id, sections: story.sections, media: story.media,
  };

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8" data-testid="story-studio">
      <div className="flex items-center justify-between gap-2 mb-3">
        <Link to="/my-stories" className="inline-flex items-center gap-1 text-sm text-primary hover:underline"><ArrowLeft className="w-4 h-4" /> My Travel Stories</Link>
        <span className="text-xs text-muted-foreground" data-testid="story-save-state">
          {saveState === "saved" ? "All changes saved" : saveState === "saving" ? "Saving…" : "Unsaved changes…"}
        </span>
      </div>

      {/* Action bar */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur border border-zinc-300 px-4 py-2.5 mb-5 flex flex-wrap items-center gap-2">
        <span className={`text-[10px] font-700 uppercase px-2 py-1 rounded-full ${published ? "bg-emerald-100 text-emerald-800" : story.status === "archived" ? "bg-zinc-200 text-zinc-600" : "bg-amber-100 text-amber-800"}`} data-testid="story-status-chip">
          {published ? `Published · ${story.visibility}` : story.status}
        </span>
        {story.language && <span className="text-[10px] font-700 uppercase px-2 py-1 rounded-full bg-sky-100 text-sky-800" data-testid="story-lang-chip">{story.language}</span>}
        <span className="text-xs text-muted-foreground truncate">{story.style} · {story.trip_title}</span>
        <select defaultValue="" disabled={translating} onChange={(e) => { if (e.target.value) { translate(e.target.value); e.target.value = ""; } }}
          data-testid="story-translate-select" className="border border-zinc-300 px-2 py-1 text-xs bg-white disabled:opacity-50" title="Create a translated copy — original stays untouched">
          <option value="">{translating ? "Translating…" : "🌐 Translate…"}</option>
          {["Malayalam", "Hindi", "Spanish", "French", "German", "Japanese", "Arabic", "Portuguese", "Tamil", "Chinese"].map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <button onClick={() => setPreview((p) => !p)} data-testid="story-preview-btn" className="inline-flex items-center gap-1 border border-zinc-300 px-3 py-1.5 text-xs font-700 bg-white">
            <Eye className="w-3.5 h-3.5" /> {preview ? "Back to editor" : "Preview"}
          </button>
          <select value={pdfTheme} onChange={(e) => setPdfTheme(e.target.value)} data-testid="story-pdf-theme" className="border border-zinc-300 px-2 py-1.5 text-xs bg-white">
            {PDF_THEMES.map((t) => <option key={t} value={t}>{t === "photobook" ? "Photo Book" : t[0].toUpperCase() + t.slice(1)} PDF</option>)}
          </select>
          <button onClick={exportPdf} data-testid="story-pdf-btn" className="inline-flex items-center gap-1 border border-zinc-300 px-3 py-1.5 text-xs font-700 bg-white">
            <FileDown className="w-3.5 h-3.5" /> Export PDF
          </button>
          {published ? (
            <button onClick={unpublish} data-testid="story-unpublish-btn" className="inline-flex items-center gap-1 border border-zinc-400 px-3 py-1.5 text-xs font-700 bg-white"><Lock className="w-3.5 h-3.5" /> Make Private</button>
          ) : (
            <button onClick={() => setPublishBox({ visibility: "public" })} data-testid="story-publish-btn" className="inline-flex items-center gap-1 bg-indigo-600 text-white px-3 py-1.5 text-xs font-700"><Globe className="w-3.5 h-3.5" /> Publish Story</button>
          )}
        </div>
      </div>

      {/* Publish / share panel */}
      {publishBox && !preview && (
        <div className="border border-indigo-300 bg-indigo-50/60 p-4 mb-5" data-testid="story-publish-panel">
          {publishBox.done ? (
            <div>
              <div className="font-700 text-sm text-emerald-800 flex items-center gap-1.5 mb-2"><Check className="w-4 h-4" /> Ready to Share — your story is {story.visibility}</div>
              {publicUrl && <a href={publicUrl} target="_blank" rel="noreferrer" data-testid="story-public-url" className="text-sm text-primary hover:underline break-all">{publicUrl}</a>}
              {(publishBox.done.warnings || []).map((w, i) => <p key={i} className="text-xs text-amber-700 mt-1">⚠ {w}</p>)}
              <div className="mt-3"><ShareButtons url={publicUrl} title={story.title} cardUrl={cardUrl} publicId={story.public_id} /></div>
              <p className="text-[11px] text-muted-foreground mt-2">Shared links show your cover photo and title as a preview card on WhatsApp, Facebook and X.</p>
            </div>
          ) : publishBox.review ? (
            <div data-testid="story-privacy-review">
              <div className="font-700 text-sm flex items-center gap-1.5 mb-1"><ShieldCheck className="w-4 h-4 text-indigo-700" /> Privacy Review — {publishBox.review.findings.length} sensitive text item{publishBox.review.findings.length === 1 ? "" : "s"}{(publishBox.review.photo_warnings || []).length > 0 ? ` · ${publishBox.review.photo_warnings.length} photo warning${publishBox.review.photo_warnings.length === 1 ? "" : "s"}` : ""}</div>
              <ul className="text-xs space-y-0.5 mb-2">
                {publishBox.review.findings.slice(0, 12).map((f, i) => <li key={i}>🔒 {f.label} <span className="text-muted-foreground">({f.preview})</span></li>)}
              </ul>
              {(publishBox.review.photo_warnings || []).length > 0 && (
                <div className="mb-2 space-y-1.5" data-testid="story-photo-warnings">
                  {publishBox.review.photo_warnings.map((w) => (
                    <div key={w.media_id} className="flex items-center gap-2 bg-white border border-amber-300 p-1.5" data-testid={`story-photo-warning-${w.media_id}`}>
                      <img src={mediaUrl(sid, w.media_id, true)} alt="" className="h-12 w-16 object-cover" />
                      <span className="text-xs text-amber-800 flex-1">⚠ This image may contain sensitive information: {w.reason}</span>
                      <button onClick={() => { removeMedia(w.media_id); setPublishBox({ ...publishBox, review: { ...publishBox.review, photo_warnings: publishBox.review.photo_warnings.filter((x) => x.media_id !== w.media_id) } }); }}
                        data-testid={`story-photo-warning-remove-${w.media_id}`} className="text-xs font-700 text-rose-700 border border-rose-300 px-2 py-1 shrink-0">Remove photo</button>
                    </div>
                  ))}
                </div>
              )}
              {(publishBox.review.warnings || []).map((w, i) => <p key={i} className="text-xs text-amber-700 mb-1">⚠ {w}</p>)}
              <div className="flex gap-2 mt-2">
                <button onClick={() => doPublish(publishBox.visibility, true)} data-testid="story-publish-acknowledge" className="bg-indigo-600 text-white px-3 py-1.5 text-xs font-700">Remove &amp; Publish {publishBox.visibility}</button>
                <button onClick={() => setPublishBox(null)} className="border border-zinc-300 px-3 py-1.5 text-xs font-700 bg-white">Keep editing</button>
              </div>
            </div>
          ) : (
            <div>
              <div className="font-700 text-sm mb-2">Who can see this story?</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {[["public", "Public — anyone can view", Globe], ["unlisted", "Unlisted — anyone with the link", Link2]].map(([v, l, Icon]) => (
                  <button key={v} onClick={() => setPublishBox({ visibility: v })} data-testid={`story-vis-${v}`}
                    className={`inline-flex items-center gap-1.5 border px-3 py-2 text-xs font-600 ${publishBox.visibility === v ? "border-indigo-600 bg-white text-indigo-800" : "border-zinc-300 bg-white"}`}>
                    <Icon className="w-3.5 h-3.5" /> {l}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mb-2">Privacy Shield runs again now. Publishing is always your explicit action — “Generate” never means “Publish”.</p>
              <div className="flex gap-2">
                <button onClick={() => doPublish(publishBox.visibility)} data-testid="story-publish-confirm" className="bg-indigo-600 text-white px-4 py-1.5 text-xs font-700">Run Privacy Shield &amp; Publish</button>
                <button onClick={() => setPublishBox(null)} className="border border-zinc-300 px-3 py-1.5 text-xs font-700 bg-white">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}

      {preview ? (
        <div className="border border-zinc-200 bg-white p-6 sm:p-10" data-testid="story-preview">
          <StoryRender view={view} withAuth />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Privacy shield summary */}
          <details className="border border-emerald-200 bg-emerald-50/50 px-4 py-3" data-testid="story-shield-summary">
            <summary className="cursor-pointer text-sm font-700 text-emerald-900 flex items-center gap-1.5 list-none">
              <ShieldCheck className="w-4 h-4" /> Privacy Shield — {findings.length === 0 ? "no sensitive information detected" : `${findings.length} sensitive item${findings.length === 1 ? "" : "s"} removed from this draft`}
            </summary>
            {findings.length > 0 && (
              <ul className="text-xs mt-2 space-y-0.5">{findings.slice(0, 20).map((f, i) => <li key={i}>🔒 {f.label} <span className="text-muted-foreground">({f.preview})</span></li>)}</ul>
            )}
            <p className="text-[11px] text-muted-foreground mt-2">Your private diary was not changed — this scan applies only to the publishing draft. It runs again before publishing and before every PDF export.</p>
          </details>

          {/* Title */}
          <div>
            <input value={story.title || ""} onChange={(e) => patch({ title: e.target.value })} data-testid="story-title-input"
              className="w-full font-heading font-900 text-2xl sm:text-3xl border-b-2 border-zinc-200 focus:border-indigo-500 outline-none bg-transparent py-1" placeholder="Story title" />
            <input value={story.subtitle || ""} onChange={(e) => patch({ subtitle: e.target.value })} data-testid="story-subtitle-input"
              className="w-full text-sm text-muted-foreground border-b border-zinc-200 focus:border-indigo-400 outline-none bg-transparent py-1 mt-1" placeholder="Subtitle (optional)" />
            {(story.title_suggestions || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                <span className="text-[11px] text-muted-foreground py-0.5">✨ AI title ideas:</span>
                {story.title_suggestions.map((t, i) => (
                  <button key={i} onClick={() => patch({ title: t })} data-testid={`story-title-sug-${i}`} className="text-[11px] border border-zinc-300 bg-white px-2 py-0.5 hover:border-indigo-400">{t}</button>
                ))}
              </div>
            )}
          </div>

          {/* Settings */}
          <div className="grid sm:grid-cols-3 gap-3 border border-zinc-200 bg-zinc-50 p-3">
            <label className="text-xs">
              <span className="font-700 block mb-1">Author shown</span>
              <input value={story.author_display || ""} onChange={(e) => patch({ author_display: e.target.value })} data-testid="story-author-input" placeholder="Anonymous Traveler" className={`${inp} w-full`} />
            </label>
            <label className="text-xs">
              <span className="font-700 block mb-1">Dates shown publicly</span>
              <select value={story.date_display_mode || "month"} onChange={(e) => patch({ date_display_mode: e.target.value })} data-testid="story-datemode" className={`${inp} w-full`}>
                <option value="exact">Exact dates</option><option value="month">Month &amp; year only</option>
                <option value="year">Year only</option><option value="hidden">Hide dates</option>
              </select>
            </label>
            <label className="text-xs">
              <span className="font-700 block mb-1">Cover photo</span>
              <select value={story.cover_media_id || ""} onChange={(e) => patch({ cover_media_id: e.target.value || null })} data-testid="story-cover-select" className={`${inp} w-full`}>
                <option value="">No cover</option>
                {(story.media || []).map((m, i) => <option key={m.id} value={m.id}>Photo {i + 1}{m.caption ? ` — ${m.caption.slice(0, 30)}` : ""}</option>)}
              </select>
            </label>
            <label className="text-xs">
              <span className="font-700 block mb-1">Comments</span>
              <select value={story.comments_mode || "off"} onChange={(e) => patch({ comments_mode: e.target.value })} data-testid="story-comments-mode" className={`${inp} w-full`}>
                <option value="off">Off</option>
                <option value="members">TravelerPulse members</option>
                <option value="public">Public (anyone)</option>
              </select>
            </label>
          </div>

          {/* Sections */}
          {story.sections.map((s, i) => (
            <SectionEditor key={s.id} s={s} i={i} total={story.sections.length} sid={sid} media={story.media || []}
              onChange={(f) => patchSection(s.id, f)} onMove={(dir) => moveSection(i, dir)}
              onDelete={() => patch({ sections: story.sections.filter((x) => x.id !== s.id) })} />
          ))}
          <button onClick={() => patch({ sections: [...story.sections, { id: crypto.randomUUID(), kind: "custom", date: null, heading: "New section", body: "", media_ids: [], links: [], video_url: null }] })}
            data-testid="story-add-section" className="inline-flex items-center gap-1 border border-dashed border-zinc-400 px-4 py-2 text-sm font-600 text-zinc-600 hover:border-indigo-500 hover:text-indigo-700">
            <Plus className="w-4 h-4" /> Add section
          </button>

          {/* Media manager */}
          {(story.media || []).length > 0 && (
            <div className="border border-zinc-200 bg-white p-4" data-testid="story-media-manager">
              <h3 className="font-700 text-sm mb-2 flex items-center gap-1.5"><ImagePlus className="w-4 h-4" /> Story photos &amp; captions</h3>
              <div className="grid sm:grid-cols-3 gap-3">
                {story.media.map((m) => (
                  <div key={m.id} className="border border-zinc-200" data-testid={`story-media-${m.id}`}>
                    <img src={mediaUrl(sid, m.id, true)} alt={m.alt || ""} className="w-full h-32 object-cover" loading="lazy" />
                    <div className="p-2 space-y-1">
                      <input value={m.caption || ""} onChange={(e) => patch({ media: story.media.map((x) => (x.id === m.id ? { ...x, caption: e.target.value, alt: e.target.value } : x)) })}
                        placeholder="Caption / alt text" data-testid={`story-media-caption-${m.id}`} className={`${inp} w-full text-xs`} />
                      <div className="flex justify-between items-center">
                        {story.cover_media_id === m.id
                          ? <span className="text-[10px] font-700 text-indigo-700 uppercase">Cover</span>
                          : <button onClick={() => patch({ cover_media_id: m.id })} className="text-[10px] text-primary hover:underline">Use as Cover</button>}
                        <button onClick={() => patch({
                          media: story.media.filter((x) => x.id !== m.id),
                          cover_media_id: story.cover_media_id === m.id ? null : story.cover_media_id,
                          sections: story.sections.map((x) => ({ ...x, media_ids: (x.media_ids || []).filter((id) => id !== m.id) })),
                        })} data-testid={`story-media-remove-${m.id}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Removing a photo here only removes it from this story — the original stays safely in your diary.</p>
            </div>
          )}

          {/* Analytics + comment moderation */}
          <StudioInsights sid={sid} published={published} commentsMode={story.comments_mode || "off"} />
        </div>
      )}
      <p className="mt-8 text-[11px] text-muted-foreground">Every published story and PDF ends with “Presented by TravelerPulse.pro™”. Your private Travel Diary is never modified or exposed.</p>
    </div>
  );
}

function SectionEditor({ s, i, total, sid, media, onChange, onMove, onDelete }) {
  const [aiBusy, setAiBusy] = useState(false);
  const [showAttach, setShowAttach] = useState(false);
  const [linkForm, setLinkForm] = useState(null);

  const assist = async (action) => {
    if (!String(s.body || "").trim()) { toast.error("Write something first"); return; }
    setAiBusy(true);
    try {
      const r = await api.post(`/stories/${sid}/ai-assist`, { action, text: s.body });
      onChange({ body: r.data.text });
      toast.success("Section updated by AI — review the result");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI assist unavailable"); }
    setAiBusy(false);
  };
  const unattached = media.filter((m) => !(s.media_ids || []).includes(m.id));

  return (
    <div className="border border-zinc-200 bg-white p-4" data-testid={`story-section-editor-${s.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <input value={s.heading || ""} onChange={(e) => onChange({ heading: e.target.value })} data-testid={`story-sec-heading-${s.id}`}
          className="flex-1 font-heading font-900 text-lg border-b border-zinc-200 focus:border-indigo-400 outline-none bg-transparent py-0.5" placeholder="Section heading" />
        {s.date && <span className="text-[10px] font-mono text-muted-foreground shrink-0">{s.date}</span>}
        <button onClick={() => onMove(-1)} disabled={i === 0} className="text-zinc-400 hover:text-zinc-800 disabled:opacity-30" data-testid={`story-sec-up-${s.id}`}><ChevronUp className="w-4 h-4" /></button>
        <button onClick={() => onMove(1)} disabled={i === total - 1} className="text-zinc-400 hover:text-zinc-800 disabled:opacity-30" data-testid={`story-sec-down-${s.id}`}><ChevronDown className="w-4 h-4" /></button>
        <button onClick={onDelete} className="text-zinc-400 hover:text-destructive" data-testid={`story-sec-del-${s.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      <textarea value={s.body || ""} onChange={(e) => onChange({ body: e.target.value })} rows={Math.min(10, Math.max(3, Math.ceil((s.body || "").length / 90)))}
        data-testid={`story-sec-body-${s.id}`} placeholder="Tell this part of your journey…"
        className="w-full border border-zinc-200 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary leading-relaxed" />
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">{aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} AI:</span>
        {AI_ACTIONS.map(([a, l]) => (
          <button key={a} onClick={() => assist(a)} disabled={aiBusy} data-testid={`story-ai-${a}-${s.id}`}
            className="text-[11px] border border-zinc-300 bg-white px-2 py-0.5 hover:border-indigo-400 disabled:opacity-50">{l}</button>
        ))}
        <span className="mx-1 text-zinc-300">|</span>
        <button onClick={() => setShowAttach((v) => !v)} data-testid={`story-sec-addphoto-${s.id}`} className="text-[11px] border border-zinc-300 bg-white px-2 py-0.5 hover:border-indigo-400 inline-flex items-center gap-1"><ImagePlus className="w-3 h-3" /> Add Photo</button>
        <button onClick={() => setLinkForm(linkForm ? null : { title: "", url: "" })} data-testid={`story-sec-addlink-${s.id}`} className="text-[11px] border border-zinc-300 bg-white px-2 py-0.5 hover:border-indigo-400 inline-flex items-center gap-1"><Link2 className="w-3 h-3" /> Add Link</button>
      </div>
      {showAttach && (
        <div className="flex flex-wrap gap-2 mt-2 border border-zinc-200 bg-zinc-50 p-2" data-testid={`story-sec-attach-${s.id}`}>
          {unattached.length === 0 ? <span className="text-xs text-muted-foreground">All story photos are already attached — manage photos below.</span> :
            unattached.map((m) => (
              <button key={m.id} onClick={() => { onChange({ media_ids: [...(s.media_ids || []), m.id] }); setShowAttach(false); }} data-testid={`story-sec-attach-pick-${m.id}`} className="border-2 border-transparent hover:border-indigo-500">
                <img src={mediaUrl(sid, m.id, true)} alt="" className="h-16 w-24 object-cover" />
              </button>
            ))}
        </div>
      )}
      {(s.media_ids || []).length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {(s.media_ids || []).map((mid) => {
            const m = media.find((x) => x.id === mid);
            return m ? (
              <div key={mid} className="relative group">
                <img src={mediaUrl(sid, mid, true)} alt="" className="h-20 w-28 object-cover border border-zinc-200" />
                <button onClick={() => onChange({ media_ids: s.media_ids.filter((x) => x !== mid) })} data-testid={`story-sec-detach-${mid}`}
                  className="absolute -top-1.5 -right-1.5 bg-white border border-zinc-300 rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X className="w-3 h-3" /></button>
              </div>
            ) : null;
          })}
        </div>
      )}
      {linkForm && (
        <div className="flex gap-1.5 mt-2">
          <input value={linkForm.title} onChange={(e) => setLinkForm({ ...linkForm, title: e.target.value })} placeholder="Link title" data-testid={`story-link-title-${s.id}`} className={`${inp} text-xs`} />
          <input value={linkForm.url} onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })} placeholder="https://…" data-testid={`story-link-url-${s.id}`} className={`${inp} text-xs flex-1`} />
          <button onClick={() => { if (!linkForm.url.trim()) return; const u = linkForm.url.startsWith("http") ? linkForm.url : `https://${linkForm.url}`; onChange({ links: [...(s.links || []), { title: linkForm.title, url: u }] }); setLinkForm(null); }}
            data-testid={`story-link-save-${s.id}`} className="px-3 bg-indigo-600 text-white text-xs font-600">Add</button>
        </div>
      )}
      {(s.links || []).length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {s.links.map((l, li) => (
            <li key={li} className="text-xs flex items-center gap-1.5">
              <Link2 className="w-3 h-3 text-muted-foreground" />
              <a href={l.url} target="_blank" rel="noreferrer" className="text-primary hover:underline truncate">{l.title || l.url}</a>
              <button onClick={() => onChange({ links: s.links.filter((_, x) => x !== li) })} className="text-muted-foreground hover:text-destructive"><X className="w-3 h-3" /></button>
            </li>
          ))}
        </ul>
      )}
      <div className="flex items-center gap-1.5 mt-2">
        <Video className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
        <input value={s.video_url || ""} onChange={(e) => onChange({ video_url: e.target.value || null })} data-testid={`story-sec-video-${s.id}`}
          placeholder="YouTube / Vimeo link (optional)" className={`${inp} text-xs flex-1`} />
      </div>
    </div>
  );
}

function StudioInsights({ sid, published, commentsMode }) {
  const [stats, setStats] = useState(null);
  const [comments, setComments] = useState([]);
  const loadComments = () => api.get(`/stories/${sid}/comments`).then((r) => setComments(r.data.items)).catch(() => {});
  useEffect(() => {
    if (published) api.get(`/stories/${sid}/analytics`).then((r) => setStats(r.data)).catch(() => {});
    loadComments();
  }, [sid, published]); // eslint-disable-line react-hooks/exhaustive-deps

  const moderate = async (cid, action) => {
    await api.post(`/stories/${sid}/comments/${cid}/moderate`, { action });
    loadComments();
    toast.success(action === "approve" ? "Comment approved" : "Comment rejected");
  };
  const remove = async (cid) => { await api.delete(`/stories/${sid}/comments/${cid}`); loadComments(); };
  const pending = comments.filter((c) => c.status === "pending");
  const others = comments.filter((c) => c.status !== "pending");

  if (!published && comments.length === 0) return null;
  return (
    <div className="space-y-4">
      {published && stats && (
        <div className="border border-zinc-200 bg-white p-4" data-testid="story-analytics">
          <h3 className="font-700 text-sm mb-3 flex items-center gap-1.5"><BarChart3 className="w-4 h-4" /> Story analytics</h3>
          <div className="grid grid-cols-3 gap-3 text-center mb-3">
            <div className="border border-zinc-200 bg-zinc-50 py-2"><div className="font-heading font-900 text-xl" data-testid="analytics-views">{stats.views}</div><div className="text-[11px] text-muted-foreground">Views</div></div>
            <div className="border border-zinc-200 bg-zinc-50 py-2"><div className="font-heading font-900 text-xl" data-testid="analytics-shares">{stats.shares_total}</div><div className="text-[11px] text-muted-foreground">Shares</div></div>
            <div className="border border-zinc-200 bg-zinc-50 py-2"><div className="font-heading font-900 text-xl">{stats.countries.length}</div><div className="text-[11px] text-muted-foreground">Reader countries</div></div>
          </div>
          {stats.shares_by_network.length > 0 && (
            <p className="text-xs text-muted-foreground mb-1">Shares: {stats.shares_by_network.map((s) => `${s.network} ${s.count}`).join(" · ")}</p>
          )}
          {stats.countries.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="analytics-countries">
              {stats.countries.map((c) => (
                <span key={c.country} className="text-[11px] border border-zinc-300 bg-zinc-50 px-2 py-0.5">{c.country} · {c.views}</span>
              ))}
            </div>
          )}
        </div>
      )}
      {(commentsMode !== "off" || comments.length > 0) && (
        <div className="border border-zinc-200 bg-white p-4" data-testid="story-moderation">
          <h3 className="font-700 text-sm mb-2 flex items-center gap-1.5">
            <MessageSquare className="w-4 h-4" /> Comments
            {pending.length > 0 && <span className="text-[10px] font-700 bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded-full">{pending.length} awaiting approval</span>}
          </h3>
          {comments.length === 0 ? (
            <p className="text-xs text-muted-foreground">No comments yet. Readers' comments appear here for your approval before going public.</p>
          ) : (
            <div className="space-y-2">
              {[...pending, ...others].map((c) => (
                <div key={c.id} className={`border px-3 py-2 ${c.status === "pending" ? "border-amber-300 bg-amber-50/50" : "border-zinc-200"}`} data-testid={`mod-comment-${c.id}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <b>{c.name}</b>
                    <span className="text-muted-foreground">{(c.created_at || "").slice(0, 10)}</span>
                    <span className={`text-[10px] font-700 uppercase px-1.5 rounded-full ${c.status === "approved" ? "bg-emerald-100 text-emerald-800" : c.status === "pending" ? "bg-amber-100 text-amber-800" : "bg-zinc-200 text-zinc-600"}`}>{c.status}</span>
                    <span className="ml-auto flex gap-1.5">
                      {c.status !== "approved" && <button onClick={() => moderate(c.id, "approve")} data-testid={`mod-approve-${c.id}`} className="text-emerald-700 font-700 hover:underline">Approve</button>}
                      {c.status !== "rejected" && <button onClick={() => moderate(c.id, "reject")} data-testid={`mod-reject-${c.id}`} className="text-amber-700 font-700 hover:underline">Reject</button>}
                      <button onClick={() => remove(c.id)} data-testid={`mod-delete-${c.id}`} className="text-rose-700 hover:underline">Delete</button>
                    </span>
                  </div>
                  <p className="text-sm mt-1 whitespace-pre-line">{c.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
